import { FC, memo, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import {
  useConvenienceDispatchAllDeliveriesQuery,
  useConvenienceDispatchDeliveriesQuery,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import {
  useConvenienceDispatchFixedRouteDetailQuery,
  useConvenienceDispatchFixedRoutesQuery,
} from 'src/hooks/useConvenienceDispatchFixedRoutes.query';
import { useApplyFixedRouteMutation } from 'src/hooks/useConvenienceDispatchMutation';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { useSelectedFixedRouteId } from 'src/hooks/useSelectedFixedRouteId';

import ConvenienceDispatchesPresenter from './Presenter';

const PER_PAGE_DELIVERIES = 10;
const PER_PAGE_LEG_RUNS = 200;

const ConvenienceDispatches: FC = memo(() => {
  const { startOn: startOnParam, endOn: endOnParam } = useParams<{ startOn: string; endOn: string }>();
  const navigate = useNavigate();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);

  // path param がない場合は今日の日付へリダイレクト
  useEffect(() => {
    if (startOnParam && endOnParam) return;
    const on =
      licenseContext.config?.on ??
      localStorage.getItem('planningOn') ??
      datetimeDecorator.toYyyyMmDd(new Date());
    void navigate(`/convenience-dispatches/${on}/${on}`, { replace: true });
  }, [startOnParam, endOnParam, licenseContext.config?.on, navigate]);

  const startOn = startOnParam ?? '';
  const endOn = endOnParam ?? startOn;

  // 選択中の固定ルート ID。日付ごとに localStorage に保存し、リロードや
  // ダッシュボード経由の遷移でも復元する。書き込み時に LRU で 30 エントリに整形する。
  const [fixedRouteId, setFixedRouteId] = useSelectedFixedRouteId(startOn);

  // V3Plans / V3Timeline と同様、現在の期間を plansContext / licenseContext に反映する
  useEffect(() => {
    if (!startOn || !endOn) return;
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    licenseContext.setStartEndOn({ startOn, endOn });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOn, endOn]);

  const { data: fixedRoutes = [], isLoading: isLoadingFixedRoutes } =
    useConvenienceDispatchFixedRoutesQuery();

  // 永続化されていた FR が既に削除済 / アクセス不可なら自動クリア
  useEffect(() => {
    if (fixedRouteId == null || isLoadingFixedRoutes) return;
    if (fixedRoutes.length === 0) return;
    if (!fixedRoutes.some((fr) => fr.id === fixedRouteId)) {
      setFixedRouteId(null);
    }
  }, [fixedRouteId, fixedRoutes, isLoadingFixedRoutes, setFixedRouteId]);

  // fixed route 選択時のみ detail を取得する (overlay 用補助データ)
  const fixedRouteDetailQuery = useConvenienceDispatchFixedRouteDetailQuery(fixedRouteId);

  const deliveriesQuery = useConvenienceDispatchDeliveriesQuery(
    startOn,
    endOn,
    fixedRouteId,
    PER_PAGE_DELIVERIES
  );

  const legRunsQuery = useInfiniteQueryShipmentOrderLegRuns(startOn, endOn, PER_PAGE_LEG_RUNS);

  // 本日なし判定専用: fixed route 選択時のみ対象日全件取得する (左カラム表示の pagination とは分離)
  const allDeliveriesQuery = useConvenienceDispatchAllDeliveriesQuery(
    startOn,
    endOn,
    fixedRouteId != null
  );

  // VehicleInfo 表示用のトラック/ドライバーのマスタ（V3Plans/Timeline と同じ期間指定 hook）
  const trucksQuery = useQueryPlanningsTrucks(startOn, endOn);
  const driversQuery = useQueryPlanningsDrivers(startOn, endOn);

  const applyFixedRoute = useApplyFixedRouteMutation();

  // Backdrop メッセージ切り替え用: 'call' = 呼び出し, 'align' = 対応づけ
  // 各 handler の冒頭でセットし、Backdrop 表示中の文言を切り替える。
  // Backdrop 非表示時は値が古くても影響しない (Presenter 側で applyChainBusy 判定により表示制御)。
  const [applyMode, setApplyMode] = useState<'call' | 'align'>('align');

  const handleDateRangeChange = useCallback(
    (start: string, end: string) => {
      if (start === '') return;
      // DateRangePicker は range で start / end の双方を渡す。end が未指定 (= 単日選択) なら start にフォールバックする。
      const safeEnd = end || start;
      void navigate(`/convenience-dispatches/${start}/${safeEnd}`, { replace: false });
    },
    [navigate]
  );

  // setFixedRouteId は useSelectedFixedRouteId(startOn) 由来で startOn 依存の closure。
  // 依存配列から外すと日付変更後も古い setter を閉じ込め、選択状態が古い日付の
  // localStorage キーへ書き込まれるため必ず依存に含める。
  const handleFixedRouteSelect = useCallback((id: number) => {
    setFixedRouteId(id);
  }, [setFixedRouteId]);

  const handleFixedRouteCreated = useCallback((newId: number) => {
    setFixedRouteId(newId);
  }, [setFixedRouteId]);

  const handleFixedRouteCleared = useCallback(() => {
    setFixedRouteId(null);
  }, [setFixedRouteId]);

  // 「呼び出し」系(toolbar の固定ルート選択) は apply (reset + shift 調整のみ) + refetch。
  // createOperations=false で backend にリクエストする。timeline 自動配車は走らせない。
  const handleApplyFixedRoute = useCallback((overrideId?: number) => {
    const targetId = overrideId ?? fixedRouteId;
    if (startOn === '' || targetId == null) return;

    setApplyMode('call');
    applyFixedRoute.mutate(
      { fixedRouteId: targetId, planningDate: startOn, createOperations: false },
      {
        onSuccess: () => {
          void Promise.all([
            deliveriesQuery.refetch(),
            legRunsQuery.refetch(),
            allDeliveriesQuery.refetch(),
          ]);
        },
      }
    );
  }, [startOn, fixedRouteId, applyFixedRoute, deliveriesQuery, legRunsQuery, allDeliveriesQuery]);

  // 「固定ルートに対応づけ」ボタンは apply (reset + shift 調整 + stop ↔ legRun マッチ) のみ。
  // createOperations=true で backend が leg_run + depot operation を operation_snapshot から
  // 復元するので、frontend から自動配車をチェーン発火させる必要は無い。
  const handleAlignToFixedRoute = useCallback((overrideId?: number) => {
    const targetId = overrideId ?? fixedRouteId;
    if (startOn === '' || targetId == null) return;

    setApplyMode('align');
    applyFixedRoute.mutate(
      { fixedRouteId: targetId, planningDate: startOn, createOperations: true },
      {
        onSuccess: () => {
          void Promise.all([
            deliveriesQuery.refetch(),
            legRunsQuery.refetch(),
            allDeliveriesQuery.refetch(),
          ]);
        },
      }
    );
  }, [startOn, fixedRouteId, applyFixedRoute, deliveriesQuery, legRunsQuery, allDeliveriesQuery]);

  const handleNavigate = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate]
  );

  return (
    <ConvenienceDispatchesPresenter
      startOn={startOn}
      endOn={endOn}
      fixedRouteId={fixedRouteId}
      fixedRoutes={fixedRoutes}
      isLoadingFixedRoutes={isLoadingFixedRoutes}
      isApplying={applyFixedRoute.isPending}
      deliveriesQuery={deliveriesQuery}
      legRunsQuery={legRunsQuery}
      fixedRouteDetailQuery={fixedRouteDetailQuery}
      allDeliveriesQuery={allDeliveriesQuery}
      trucks={trucksQuery.data ?? []}
      drivers={driversQuery.data ?? []}
      onDateRangeChange={handleDateRangeChange}
      onFixedRouteSelect={handleFixedRouteSelect}
      onFixedRouteCreated={handleFixedRouteCreated}
      onFixedRouteCleared={handleFixedRouteCleared}
      onApplyFixedRoute={handleApplyFixedRoute}
      onAlignToFixedRoute={handleAlignToFixedRoute}
      applyMode={applyMode}
      onNavigate={handleNavigate}
    />
  );
});

export default ConvenienceDispatches;
